import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtDate } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, ArrowDownLeft, ArrowUpRight as ArrowOut, Plus } from "lucide-react";

const Dashboard = () => {
  const [accounts, setAccounts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [prices, setPrices] = useState([]);

  const load = async () => {
    const [a, t, w, p] = await Promise.all([
      api.get("/accounts"),
      api.get("/transactions"),
      api.get("/crypto/wallet").catch(() => ({ data: [] })),
      api.get("/crypto/prices").catch(() => ({ data: [] })),
    ]);
    setAccounts(a.data); setTxns(t.data); setCrypto(w.data); setPrices(p.data);
  };

  useEffect(() => { load(); }, []);

  const total = accounts.reduce((s, a) => s + a.balance, 0);
  const cryptoValue = crypto.reduce((s, h) => {
    const p = prices.find(x => x.symbol === h.symbol);
    return s + h.quantity * (p?.price_usd || 0);
  }, 0);

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[hsl(var(--border))] border border-[hsl(var(--border))]">
        <div className="bg-[hsl(var(--surface))] p-8" data-testid="metric-net-worth">
          <div className="overline">Net worth</div>
          <div className="font-mono text-4xl mt-3">{fmtUSD(total + cryptoValue)}</div>
          <div className="overline mt-3 text-[hsl(var(--text-muted))]">Across all accounts</div>
        </div>
        <div className="bg-[hsl(var(--surface))] p-8" data-testid="metric-deposits">
          <div className="overline">Deposits</div>
          <div className="font-mono text-4xl mt-3">{fmtUSD(total)}</div>
          <div className="overline mt-3 text-[hsl(var(--text-muted))]">{accounts.length} account(s)</div>
        </div>
        <div className="bg-[hsl(var(--surface))] p-8" data-testid="metric-crypto">
          <div className="overline">Crypto portfolio</div>
          <div className="font-mono text-4xl mt-3">{fmtUSD(cryptoValue)}</div>
          <div className="overline mt-3 text-[hsl(var(--text-muted))]">{crypto.length} asset(s)</div>
        </div>
      </div>

      <section>
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="overline">01</div>
            <h2 className="font-display text-2xl mt-1">Your accounts</h2>
          </div>
          <Link to="/app/accounts" className="text-sm hover:text-[hsl(var(--primary))] flex items-center gap-1" data-testid="link-manage-accounts">
            Manage <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map((a) => (
            <div key={a.id} className="card-tech p-6" data-testid={`account-card-${a.id}`}>
              <div className="flex items-center justify-between">
                <span className="overline">{a.type}</span>
                {a.frozen && <span className="overline text-[hsl(var(--destructive))]">FROZEN</span>}
              </div>
              <div className="font-display text-xl mt-3">{a.name}</div>
              <div className="font-mono text-3xl mt-4">{fmtUSD(a.balance)}</div>
              <div className="font-mono text-xs text-[hsl(var(--text-muted))] mt-2">
                ···· {a.account_number.slice(-4)}
              </div>
            </div>
          ))}
          {accounts.length === 0 && (
            <Link to="/app/accounts" className="card-tech p-6 flex items-center justify-center gap-2 text-[hsl(var(--text-muted))]" data-testid="empty-add-account">
              <Plus className="w-4 h-4" /> Open your first account
            </Link>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="overline">02</div>
            <h2 className="font-display text-2xl mt-1">Recent activity</h2>
          </div>
          <Link to="/app/transactions" className="text-sm hover:text-[hsl(var(--primary))] flex items-center gap-1" data-testid="link-view-all-txns">
            View all <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
          <table className="w-full text-sm">
            <thead className="border-b border-[hsl(var(--border))]">
              <tr className="text-left">
                <th className="overline py-3 px-4">Date</th>
                <th className="overline py-3 px-4">Type</th>
                <th className="overline py-3 px-4">Description</th>
                <th className="overline py-3 px-4 text-right">Amount</th>
                <th className="overline py-3 px-4 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {txns.slice(0, 8).map((t) => (
                <tr key={t.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-alt))]" data-testid={`txn-row-${t.id}`}>
                  <td className="py-3 px-4 font-mono text-xs">{fmtDate(t.created_at)}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1.5">
                      {t.amount >= 0 ? <ArrowDownLeft className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> : <ArrowOut className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />}
                      {t.type.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="py-3 px-4">{t.description}</td>
                  <td className={`py-3 px-4 text-right font-mono ${t.amount >= 0 ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--text-main))]"}`}>
                    {t.amount >= 0 ? "+" : ""}{fmtUSD(t.amount)}
                  </td>
                  <td className="py-3 px-4 text-right font-mono">{fmtUSD(t.balance_after)}</td>
                </tr>
              ))}
              {txns.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-[hsl(var(--text-muted))]">No transactions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="overline">03 · Market</div>
            <h2 className="font-display text-2xl mt-1">Live crypto prices</h2>
          </div>
          <Link to="/app/crypto" className="text-sm hover:text-[hsl(var(--primary))] flex items-center gap-1" data-testid="link-trade-crypto">
            Trade <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--border))] border border-[hsl(var(--border))]">
          {prices.map((p) => (
            <div key={p.symbol} className="bg-[hsl(var(--surface))] p-6" data-testid={`price-card-${p.symbol}`}>
              <div className="overline">{p.symbol}</div>
              <div className="font-display text-lg mt-1">{p.name}</div>
              <div className="font-mono text-2xl mt-3">{fmtUSD(p.price_usd)}</div>
              <div className={`text-xs mt-1 inline-flex items-center gap-1 ${p.change_24h >= 0 ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--accent))]"}`}>
                <TrendingUp className="w-3 h-3" />
                {p.change_24h >= 0 ? "+" : ""}{p.change_24h.toFixed(2)}%
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
